import { useState } from 'react';
import { Box, Avatar, Menu, MenuItem, Typography, CircularProgress, Tooltip, IconButton } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { SvgIconComponent } from '@mui/icons-material';

import logo from '@src/assets/logo.svg';
import { colors } from '@src/styles';
import { UserData } from '../Layout/Layout';
import { NavigationPage, NAVIGATION_ITEMS } from '@src/constants/navigation';
import { getEnvironmentLabel } from '@src/constants/environment';

export type TCurrentPages = NavigationPage | 'home';

export type TSidebarView = {
  currentPage: TCurrentPages;
  userData: UserData | null;
  isLoadingUser: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isMobile: boolean;
  isMobileOpen: boolean;
  onOpenMobile: () => void;
  onCloseMobile: () => void;
  onNavigateTo: (page: TCurrentPages) => void;
  onLogout: () => void;
  onNavigateToProfile: () => void;
};

export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const SIDEBAR_EXPANDED_WIDTH = 232;
export const MOBILE_TOPBAR_HEIGHT = 56;
const ITEM_HEIGHT = 44;

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";
const monoFontFamily = "'JetBrains Mono', monospace";

const getLabelSX = (isExpanded: boolean) =>
  ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity: isExpanded ? 1 : 0,
    transition: 'opacity 0.15s ease',
  }) as const;

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

type TSidebarItem = {
  id: TCurrentPages;
  label: string;
  icon: SvgIconComponent;
  isMuted?: boolean;
};

const SIDEBAR_ITEMS: TSidebarItem[] = [
  { id: 'home', label: 'Início', icon: HomeRoundedIcon },
  ...NAVIGATION_ITEMS.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    isMuted: item.id === 'org-config',
  })),
];

const focusRingSX = {
  '&:focus-visible': { outline: `2px solid ${colors.primary.yellow}`, outlineOffset: '-2px' },
} as const;

const SidebarItem = ({
  label,
  icon: IconComponent,
  isActive = false,
  isMuted = false,
  isExpanded,
  onClick,
}: {
  label: string;
  icon: SvgIconComponent;
  isActive?: boolean;
  isMuted?: boolean;
  isExpanded: boolean;
  onClick: () => void;
}) => {
  const idleColor = isMuted ? colors.brand.textMuted : colors.brand.textSecondary;

  return (
    <Tooltip title={isExpanded ? '' : label} placement="right" arrow disableInteractive>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          width: '100%',
          height: ITEM_HEIGHT,
          paddingX: '11px',
          border: 'none',
          borderRadius: '10px',
          cursor: 'pointer',
          backgroundColor: isActive ? 'rgba(255, 160, 21, 0.1)' : 'transparent',
          color: isActive ? colors.primary.white : idleColor,
          fontFamily: titleFontFamily,
          fontSize: '14px',
          fontWeight: isActive ? 600 : isMuted ? 400 : 500,
          textAlign: 'left',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            backgroundColor: isActive ? 'rgba(255, 160, 21, 0.14)' : 'rgba(255, 255, 255, 0.04)',
            color: isMuted && !isActive ? colors.brand.textSecondary : colors.primary.white,
          },
          ...focusRingSX,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -10,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: '0 3px 3px 0',
            backgroundColor: isActive ? colors.primary.yellow : 'transparent',
          },
        }}
      >
        <IconComponent sx={{ fontSize: 22, flexShrink: 0, color: isActive ? colors.primary.yellow : 'inherit' }} />
        <Box component="span" sx={getLabelSX(isExpanded)}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
};

export const SidebarView = ({
  currentPage,
  userData,
  isLoadingUser,
  isExpanded: isExpandedPreference,
  onToggleExpanded,
  isMobile,
  isMobileOpen,
  onOpenMobile,
  onCloseMobile,
  onNavigateTo,
  onLogout,
  onNavigateToProfile,
}: TSidebarView) => {
  const isExpanded = isMobile || isExpandedPreference;
  const handleNavigate = (page: TCurrentPages) => {
    onNavigateTo(page);
    if (isMobile) onCloseMobile();
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const environment = getEnvironmentLabel();
  const environmentColor = environment === 'HML' ? colors.primary.yellow : colors.primary.blue;
  const environmentName = environment === 'PROD' ? 'Produção' : environment === 'HML' ? 'Homologação' : 'Local';
  const labelSX = getLabelSX(isExpanded);

  const handleCloseMenu = () => setAnchorEl(null);

  return (
    <>
      {isMobile && (
        <Box
          component="header"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            height: MOBILE_TOPBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            paddingX: 1,
            backgroundColor: 'rgba(10, 13, 15, 0.88)',
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${colors.brand.line}`,
          }}
        >
          <IconButton
            onClick={onOpenMobile}
            aria-label="Abrir menu"
            aria-expanded={isMobileOpen}
            sx={{ width: 44, height: 44, color: colors.primary.white }}
          >
            <MenuRoundedIcon />
          </IconButton>
          <Box component="img" src={logo} alt="" sx={{ width: 22, height: 23 }} />
          <Typography
            sx={{ fontFamily: titleFontFamily, fontSize: '15px', fontWeight: 600, color: colors.primary.white }}
          >
            Solutions
          </Typography>
          <Box
            aria-label={`Ambiente: ${environmentName}`}
            sx={{
              marginLeft: 'auto',
              marginRight: 1,
              fontFamily: monoFontFamily,
              fontSize: '10px',
              letterSpacing: '0.16em',
              color: environmentColor,
              border: `1px solid ${environmentColor}`,
              borderRadius: '999px',
              padding: '3px 8px',
            }}
          >
            {environment}
          </Box>
        </Box>
      )}

      {isMobile && (
        <Box
          onClick={onCloseMobile}
          aria-hidden
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1150,
            backgroundColor: 'rgba(10, 13, 15, 0.55)',
            backdropFilter: 'blur(4px)',
            opacity: isMobileOpen ? 1 : 0,
            pointerEvents: isMobileOpen ? 'auto' : 'none',
            transition: 'opacity 0.22s ease',
          }}
        />
      )}

      <Box
        component="aside"
        aria-label="Menu principal"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1200,
          width: isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          paddingY: 2,
          backgroundColor: colors.background.primary,
          borderRight: `1px solid ${colors.brand.line}`,
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: 'width 0.18s ease',
          ...(isMobile && {
            width: SIDEBAR_EXPANDED_WIDTH,
            maxWidth: '85vw',
            transform: isMobileOpen ? 'translateX(0)' : 'translateX(-100%)',
            visibility: isMobileOpen ? 'visible' : 'hidden',
            boxShadow: isMobileOpen ? '16px 0 48px rgba(0, 0, 0, 0.5)' : 'none',
            transition: 'transform 0.24s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.24s ease, visibility 0.24s',
          }),
        }}
      >
        <Tooltip title={isExpanded ? '' : 'Início'} placement="right" arrow disableInteractive>
          <Box
            component="button"
            type="button"
            onClick={() => handleNavigate('home')}
            aria-label="Ir para o início"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              height: 48,
              marginX: '10px',
              paddingX: '9px',
              border: 'none',
              borderRadius: '10px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              ...focusRingSX,
            }}
          >
            <Box component="img" src={logo} alt="" sx={{ width: 26, height: 27, flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                ...labelSX,
                fontFamily: titleFontFamily,
                fontSize: '16px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: colors.primary.white,
              }}
            >
              Solutions
            </Box>
          </Box>
        </Tooltip>

        <Box
          component="nav"
          aria-label="Módulos"
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 3, paddingX: '10px' }}
        >
          {SIDEBAR_ITEMS.map((item) => (
            <SidebarItem
              key={item.id}
              label={item.label}
              icon={item.icon}
              isMuted={item.isMuted}
              isActive={currentPage === item.id}
              isExpanded={isExpanded}
              onClick={() => handleNavigate(item.id)}
            />
          ))}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ paddingX: '10px', marginBottom: 1 }}>
          {isMobile ? (
            <SidebarItem label="Fechar menu" icon={CloseRoundedIcon} isExpanded onClick={onCloseMobile} />
          ) : (
            <SidebarItem
              label={isExpanded ? 'Recolher menu' : 'Expandir menu'}
              icon={isExpanded ? KeyboardDoubleArrowLeftIcon : KeyboardDoubleArrowRightIcon}
              isExpanded={isExpanded}
              onClick={onToggleExpanded}
            />
          )}
        </Box>

        <Tooltip title={isExpanded ? '' : `${environment} · ${environmentName}`} placement="right" arrow>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              height: 32,
              marginX: '10px',
              paddingX: '11px',
              marginBottom: 1,
            }}
          >
            <Box sx={{ width: 22, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '999px', backgroundColor: environmentColor }} />
            </Box>
            <Typography
              sx={{
                ...labelSX,
                fontFamily: monoFontFamily,
                fontSize: '10px',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: environmentColor,
              }}
            >
              {environment} · {environmentName}
            </Typography>
          </Box>
        </Tooltip>

        <Box sx={{ marginX: '10px', paddingTop: 1, borderTop: `1px solid ${colors.brand.line}` }}>
          {isLoadingUser ? (
            <Box sx={{ height: ITEM_HEIGHT + 8, display: 'flex', alignItems: 'center', paddingX: '13px' }}>
              <CircularProgress size={18} sx={{ color: colors.primary.yellow }} />
            </Box>
          ) : userData ? (
            <Tooltip title={isExpanded ? '' : userData.name} placement="right" arrow disableInteractive>
              <Box
                component="button"
                type="button"
                onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
                aria-label="Abrir menu do usuário"
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  height: ITEM_HEIGHT + 8,
                  paddingX: '6px',
                  border: 'none',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
                  ...focusRingSX,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    backgroundColor: colors.brand.indigo,
                    color: colors.primary.white,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {getInitials(userData.name)}
                </Avatar>
                <Box sx={{ ...labelSX, flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontFamily: titleFontFamily, fontSize: '13px', fontWeight: 600, color: colors.primary.white }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '11.5px', color: colors.brand.textMuted }}>
                    {userData.email}
                  </Typography>
                </Box>
                <UnfoldMoreIcon sx={{ ...labelSX, fontSize: 18, color: colors.brand.textMuted }} />
              </Box>
            </Tooltip>
          ) : (
            <SidebarItem label="Sair" icon={LogoutIcon} isExpanded={isExpanded} onClick={onLogout} />
          )}
        </Box>

        {userData && (
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleCloseMenu}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: {
                  marginLeft: 1.5,
                  minWidth: 240,
                  backgroundColor: colors.brand.surface,
                  backgroundImage: 'none',
                  border: `1px solid ${colors.brand.line}`,
                  borderRadius: '10px',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
                },
              },
            }}
            MenuListProps={{ sx: { padding: 0 } }}
          >
            <Box sx={{ padding: '12px 16px', borderBottom: `1px solid ${colors.brand.line}` }}>
              <Typography sx={{ color: colors.primary.white, fontSize: '14px', fontWeight: 600 }}>
                {userData.name}
              </Typography>
              <Typography sx={{ color: colors.brand.textSecondary, fontSize: '12px' }}>{userData.email}</Typography>
              {userData.member?.team && (
                <Box
                  sx={{
                    display: 'inline-block',
                    marginTop: 1,
                    padding: '3px 8px',
                    borderRadius: '999px',
                    backgroundColor: 'rgba(255, 160, 21, 0.14)',
                    color: colors.primary.yellow,
                    fontFamily: monoFontFamily,
                    fontSize: '10px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}
                >
                  {userData.member.team}
                </Box>
              )}
            </Box>
            {!!userData.member && (
              <MenuItem
                onClick={() => {
                  handleCloseMenu();
                  onNavigateToProfile();
                }}
                sx={{
                  gap: 1.25,
                  padding: '10px 16px',
                  color: colors.primary.white,
                  fontSize: '13.5px',
                  borderBottom: `1px solid ${colors.brand.line}`,
                  '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.1)' },
                }}
              >
                <PersonIcon sx={{ fontSize: 18 }} />
                Profile
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                onLogout();
              }}
              sx={{
                gap: 1.25,
                padding: '10px 16px',
                color: colors.primary.yellow,
                fontSize: '13.5px',
                '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.1)' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              Logout
            </MenuItem>
          </Menu>
        )}
      </Box>
    </>
  );
};
